import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { ApiService, HttpMethod } from '../common/api.service';
import { SasTokenModel } from './sas-token.model';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { StockImage } from './stock-image-model';
import { Member } from '../member/member.model';


@Injectable({
    providedIn: 'root'
})
export class ImageApiService extends ApiService
{
    private pendingCalls: any[] = [];
    private MAX_CONCURRENT_IMAGE_REQUESTS = 1;

    constructor(http: HttpClient)
    {
        super(http);
    }

    getSasToken(filename: string): Observable<SasTokenModel>
    {
        return this.apiRequest<SasTokenModel>(this.getSasTokenUrl(filename), HttpMethod.Get).pipe(map((json: any) => new SasTokenModel(json)));
    }

    uploadFile(file: File, sasUrl: string)
    {
        const headers = new HttpHeaders()
            .set('x-ms-blob-type', 'BlockBlob')
            .set('x-ms-blob-content-type', file.type)
            .set('Content-Type', file.type);
        const req = new HttpRequest('put', sasUrl, file, { headers: headers, reportProgress: false });
        return this.http.request(req)
            .pipe(catchError(err =>
            {
                let errorMessage = '';
                if (err.error instanceof ErrorEvent)
                {
                    errorMessage = `Error: ${err.error.message}`;
                }
                else
                {
                    errorMessage = `Server returned ${err.status} with message ${err.message}`;
                }
                return throwError(errorMessage);
            }));
    }

    downloadFile(url: string)
    {
        // const headers = new HttpHeaders()
        //     .set('x-ms-blob-type', 'BlockBlob')
        //     .set('Content-Type', 'image/*');
        return this.apiGetRequestForBlob(url);
        // return this.http.get(url, { headers: headers, responseType: 'blob' as 'json' });
    }

    getImageWithSize(imageId: number, width: number, height: number)
    {
        const pendingCall = { imageId: imageId, width: width, height: height, processing: false, observer: null };
        this.pendingCalls.push(pendingCall);
        return new Observable(observer =>
        {
            // @ts-ignore
            pendingCall.observer = observer;
            if (this.pendingCalls.length > this.MAX_CONCURRENT_IMAGE_REQUESTS) { return; } // We will process later

            this.processPendingCall(pendingCall);
        });
    }

    updateCaption(id: number, caption?: string)
    {
        return this.apiRequest<any>(this.updateImageCaption(id), HttpMethod.Put, JSON.stringify( {
            Id: id,
            Caption: caption
        }));
    }

    getStockImages(): Observable<StockImage[]>
    {
        return this.apiRequest<any>(this.getStockImagesUrl, HttpMethod.Get)
            .pipe(map((json: any) => json.map((json: any) => new StockImage(json))));
    }

    private processPendingCall(pendingCall: any)
    {
        pendingCall.processing = true;
        this.apiRequest<any>(this.getImageUrlWithSizeUrl(pendingCall.imageId, pendingCall.width, pendingCall.height), HttpMethod.Get)
            .subscribe(response =>
            {
                this.pendingCalls = this.pendingCalls.filter(call => call.imageId !== pendingCall.imageId || call.width !== pendingCall.width || call.height !== pendingCall.height);
                pendingCall.observer.next(response);
                pendingCall.observer.complete();

                this.checkForPendingCall();
            }, error =>
            {
                this.pendingCalls = this.pendingCalls.filter(call => call.imageId !== pendingCall.imageId || call.width !== pendingCall.width || call.height !== pendingCall.height);
                pendingCall.observer.error(error);
                this.checkForPendingCall();
            });
    }

    private checkForPendingCall()
    {
        // Get the next one that is pending
        const nextCall = this.pendingCalls.find(call => !call.processing);
        if (nextCall != null)
        {
            this.processPendingCall(nextCall);
        }
    }

    // =========================================================================================================================================================
    // URLs
    // =========================================================================================================================================================
    /* tslint:disable:member-ordering */
    private get baseImagesApiUrl(): string { return `${this.baseApiUrl}images`; }
    getSasTokenUrl(filename: string): string { return `${this.baseImagesApiUrl}/sas/${filename}`; }
    // getImageWithSizeUrl(imageId: number, width: number, height: number): string { return `${this.baseImagesApiUrl}/${imageId}/${width}/${height}/filename`; }
    getImageUrlWithSizeUrl(imageId: number, width: number, height: number): string { return `${this.baseImagesApiUrl}/url/${imageId}/${width}/${height}/filename`; }
    private get getStockImagesUrl(): string { return `${this.baseImagesApiUrl}/stockImages`; }
    updateImageCaption(imageId: number): string { return `${this.baseImagesApiUrl}/${imageId}/updateCaption`; }
}
